import React, { useState, useContext } from 'react';
import { ProductContext } from '../../providers/ProductContext';
import { AuthContext } from '../../providers/AuthContext';
import { showError, showSuccess } from '../../utils/sweetAlert';
import { getAdminAuthHeaders } from '../../utils/adminApi';

const BulkImport = () => {
  const { refetchProducts } = useContext(ProductContext);
  const { token } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [jsonData, setJsonData] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [importSettings, setImportSettings] = useState({
    skipDuplicates: true,
    updateExisting: false,
    validateCategories: true,
    generateSKUs: true
  });
  const [activeTab, setActiveTab] = useState('upload');

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile && uploadedFile.type === 'application/json') {
      setFile(uploadedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          setJsonData(content);
          JSON.parse(content);
          setActiveTab('editor');
        } catch (error) {
          showError('Error: El archivo no contiene JSON válido');
          setFile(null);
          setJsonData('');
        }
      };
      reader.readAsText(uploadedFile);
    } else {
      showError('Por favor, selecciona un archivo JSON válido');
    }
  };

  const validateJson = () => {
    try {
      const data = JSON.parse(jsonData);
      if (!data.products || !Array.isArray(data.products)) {
        throw new Error('El JSON debe contener un array "products"');
      }
      return data;
    } catch (error) {
      throw new Error(`JSON inválido: ${error.message}`);
    }
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      setResults(null);
      const data = validateJson();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/bulk-import`, {
        method: 'POST',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ products: data.products, importSettings: { ...importSettings, ...data.importSettings } }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Error en la importación');

      setResults(result.results);

      if (result.results.successful > 0) {
        await refetchProducts();
      }
    } catch (error) {
      showError(`Error en la importación: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = {
      products: [{
        name: "Producto Ejemplo", description: "Descripción del producto ejemplo",
        category: "Ropa", subcategory: "Camisetas", basePrice: 29.99, stock: 25,
        discount: 0, brand: "MiMarca", material: "Algodón", weight: 200,
        dimensions: { length: 70, width: 50, height: 2 },
        tags: ["ejemplo", "template"],
        hasVariants: true,
        variants: [{ size: "M", color: "Blanco", price: 29.99, images: ["https://ejemplo.com/imagen.jpg"], isActive: true }],
        images: ["https://ejemplo.com/imagen-principal.jpg"], isActive: true
      }],
      importSettings: { skipDuplicates: true, updateExisting: false, validateCategories: true, generateSKUs: true }
    };
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_import_template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const SETTINGS_ITEMS = [
    { key: 'skipDuplicates', label: 'Omitir productos duplicados', icon: 'fa-copy' },
    { key: 'updateExisting', label: 'Actualizar productos existentes', icon: 'fa-arrows-rotate' },
    { key: 'validateCategories', label: 'Validar categorías existentes', icon: 'fa-check-double' },
    { key: 'generateSKUs', label: 'Generar SKUs automáticamente', icon: 'fa-barcode' },
  ];

  const STEPS = [
    { num: '1', text: 'Descarga el template JSON o prepara tu archivo' },
    { num: '2', text: 'Completa los datos e indica URLs/rutas de imágenes ya cargadas' },
    { num: '3', text: 'Configura las opciones de importación' },
    { num: '4', text: 'Sube el archivo o pega el contenido JSON' },
    { num: '5', text: 'Haz clic en "Importar Productos"' },
  ];

  return (
    <div className="bulkimp-panel">
      {/* Header */}
      <div className="bulkimp-header">
        <div>
          <h1>Importación JSON</h1>
          <p>Importa productos desde archivos JSON. Para agrupar fotos y crear fichas visualmente, usa la pestaña “Carga Visual”.</p>
        </div>
        <button className="bulkimp-template-btn" onClick={downloadTemplate}>
          <i className="fas fa-download"></i> Descargar Template
        </button>
      </div>

      {/* Steps */}
      <div className="bulkimp-steps">
        {STEPS.map((step) => (
          <div key={step.num} className="bulkimp-step">
            <span className="bulkimp-step-num">{step.num}</span>
            <span>{step.text}</span>
          </div>
        ))}
      </div>

      {/* Settings */}
      <div className="bulkimp-settings-card">
        <div className="bulkimp-settings-header">
          <i className="fas fa-sliders"></i>
          <h3>Configuración</h3>
        </div>
        <div className="bulkimp-settings-grid">
          {SETTINGS_ITEMS.map(item => (
            <label key={item.key} className="bulkimp-setting-item">
              <input
                type="checkbox"
                checked={importSettings[item.key]}
                onChange={(e) => setImportSettings({ ...importSettings, [item.key]: e.target.checked })}
              />
              <span className="bulkimp-setting-check">
                <i className="fas fa-check"></i>
              </span>
              <i className={`fas ${item.icon} bulkimp-setting-icon`}></i>
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Tabs: Upload / Editor */}
      <div className="bulkimp-tabs">
        <button className={`bulkimp-tab ${activeTab === 'upload' ? 'active' : ''}`} onClick={() => setActiveTab('upload')}>
          <i className="fas fa-upload"></i> Subir Archivo
        </button>
        <button className={`bulkimp-tab ${activeTab === 'editor' ? 'active' : ''}`} onClick={() => setActiveTab('editor')}>
          <i className="fas fa-code"></i> Editor JSON
        </button>
      </div>

      {activeTab === 'upload' && (
        <div className="bulkimp-upload-card">
          <div className="bulkimp-dropzone" onClick={() => document.getElementById('bulkimp-file').click()}>
            <i className="fas fa-cloud-arrow-up"></i>
            <h3>{file ? file.name : 'Arrastra un archivo JSON aquí'}</h3>
            <p>{file ? '✅ Archivo cargado correctamente' : 'o haz clic para seleccionar'}</p>
            <input id="bulkimp-file" type="file" accept=".json" onChange={handleFileUpload} style={{ display: 'none' }} />
          </div>
        </div>
      )}

      {activeTab === 'editor' && (
        <div className="bulkimp-editor-card">
          <textarea
            className="bulkimp-textarea"
            rows="18"
            value={jsonData}
            onChange={(e) => setJsonData(e.target.value)}
            placeholder='{\n  "products": [\n    {\n      "name": "Mi Producto",\n      "basePrice": 1500,\n      "category": "Remeras"\n    }\n  ]\n}'
          />
        </div>
      )}

      {/* Import Button */}
      <div className="bulkimp-action-row">
        <button className="bulkimp-import-btn" onClick={handleImport} disabled={importing || !jsonData.trim()}>
          {importing ? (
            <><i className="fas fa-spinner fa-spin"></i> Importando...</>
          ) : (
            <><i className="fas fa-rocket"></i> Importar Productos</>
          )}
        </button>
      </div>

      {/* Results */}
      {results && (
        <div className="bulkimp-results">
          <div className="bulkimp-results-header">
            <i className="fas fa-chart-bar"></i>
            <h3>Resultados de la Importación</h3>
          </div>

          <div className="bulkimp-results-kpis">
            <div className="bulkimp-result-kpi">
              <span className="bulkimp-result-value" style={{ color: '#3b82f6' }}>{results.total}</span>
              <span className="bulkimp-result-label">Total</span>
            </div>
            <div className="bulkimp-result-kpi">
              <span className="bulkimp-result-value" style={{ color: '#10b981' }}>{results.successful}</span>
              <span className="bulkimp-result-label">Exitosos</span>
            </div>
            <div className="bulkimp-result-kpi">
              <span className="bulkimp-result-value" style={{ color: '#f59e0b' }}>{results.skipped}</span>
              <span className="bulkimp-result-label">Omitidos</span>
            </div>
            <div className="bulkimp-result-kpi">
              <span className="bulkimp-result-value" style={{ color: '#ef4444' }}>{results.failed}</span>
              <span className="bulkimp-result-label">Fallidos</span>
            </div>
          </div>

          {results.created?.length > 0 && (
            <div className="bulkimp-result-list success">
              <h4><i className="fas fa-check-circle"></i> Productos creados</h4>
              {results.created.map((name, i) => <span key={i} className="bulkimp-result-item">{name}</span>)}
            </div>
          )}
          {results.updated?.length > 0 && (
            <div className="bulkimp-result-list info">
              <h4><i className="fas fa-arrows-rotate"></i> Productos actualizados</h4>
              {results.updated.map((name, i) => <span key={i} className="bulkimp-result-item">{name}</span>)}
            </div>
          )}
          {results.errors?.length > 0 && (
            <div className="bulkimp-result-list error">
              <h4><i className="fas fa-exclamation-circle"></i> Errores encontrados</h4>
              <table className="bulkimp-error-table">
                <thead><tr><th>#</th><th>Producto</th><th>Error</th></tr></thead>
                <tbody>
                  {results.errors.map((err, i) => (
                    <tr key={i}><td>{err.index + 1}</td><td>{err.product}</td><td>{err.error}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkImport;
